/**
 * useSearch - Core search logic hook for GlobalSearch
 *
 * Handles: debounced search, local job filtering, API search,
 * keyboard navigation, recent searches, and filter state.
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import type {
  SearchResult,
  LocalJob,
  QuickAction,
  SearchProvider,
} from './types'
import {
  computeFacets,
  filterByFacet,
  type Facet,
  type FacetKey,
} from './facets'
import { sectionOrder } from './types'
import { getActiveFindController } from '../find/findScopeRegistry'
import { emitSearchTelemetry } from './telemetry'
import {
  loadFrecency,
  frecencyKey,
  frecencyScore,
  recordFrecencySelection,
  type FrecencyMap,
} from './frecency'
import { getCachedResults, setCachedResults } from './queryCache'
import { tokenizeSearchQuery, allTokensMatchFields } from './matching'

/**
 * Per-provider error wrapper so `Promise.allSettled` can carry
 * the offending provider id back to the caller. Kept module-local
 * (not exported) — consumers only need the error message we put
 * into `searchError`, not the class itself.
 */
class ProviderError extends Error {
  providerId: string
  cause: unknown
  constructor(providerId: string, cause: unknown) {
    super(`Search provider "${providerId}" failed`)
    this.providerId = providerId
    this.cause = cause
  }
}

const RECENT_SEARCHES_KEY = 'quotech-recent-searches'
const RECENT_SEARCHES_MAX = 8
const RECENT_SEARCH_MIN_LEN = 2

/** Pure I→O — prepend a query, dedupe, cap length. */
export function pushRecentSearch(
  prev: string[],
  query: string,
  max = RECENT_SEARCHES_MAX,
): string[] {
  const trimmed = query.trim()
  if (trimmed.length < RECENT_SEARCH_MIN_LEN) return prev
  return [trimmed, ...prev.filter((s) => s !== trimmed)].slice(0, max)
}

interface UseSearchOptions {
  isOpen: boolean
  onClose: () => void
  onSearch?: (query: string, results: SearchResult[]) => void
  apiClient?: {
    post: (url: string, data: any) => Promise<any>
    get: (url: string) => Promise<any>
  }
  localJobs: LocalJob[]
  quickActions: QuickAction[]
  /** Module-supplied search providers (PR 3b). Optional — when
   *  empty `useSearch` only consults `apiClient` + `localJobs`,
   *  preserving the pre-PR-3b behaviour. */
  searchProviders?: SearchProvider[]
  /** Stack 11 PR 2 — initial set of active filter chip ids. When
   *  non-empty, the popup re-applies them every time it opens
   *  AND immediately reveals the filter row so the user sees what
   *  they've been narrowed to. Used by the Cmd+F advanced search
   *  popup to pre-scope to ``['email','card','board','lane']``.
   */
  initialActiveFilters?: string[]
  /** BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — see
   *  GlobalSearchProps.initialQuery. Seeds the query input on
   *  ``isOpen → true``. Hosts must clear it in their own
   *  ``onClose`` handler so a manual ⌘F re-open starts blank. */
  initialQuery?: string
}

// Module-level stable defaults. Destructured defaults like
// `searchProviders = []` allocate a fresh array on every render — that
// reference-instability would invalidate `performSearch`'s memoization
// and re-fire its consumer effect on every render, which (when the
// query is empty) hits `setResults([])` → re-render → loop.
// "Maximum update depth exceeded" reproduced reliably when callers
// omitted `searchProviders`.
const EMPTY_PROVIDERS: ReadonlyArray<SearchProvider> = Object.freeze([])

// Navigation is a META layer, not a content type. A provider that
// answers "where can I go" (the nav / hub / Pages provider) is always
// consulted and its results are never stripped by the content-type
// chips (Emails / Cards / …). This is why typing "email" surfaces the
// Email HUB even when the popup is pre-scoped to ['email','card',…]:
// the operator narrowing CONTENT should not hide a navigation target.
// Strong nav matches float to the very top via the `pages` section
// (see SEARCH_SECTIONS in types.ts + createNavSearchProvider).
const NAV_PROVIDER_IDS = new Set(['navigation', 'pages'])
const NAV_RESULT_TYPES = new Set(['page', 'nav', 'navigation'])

/** True when a provider is the navigation/hub/Pages meta-provider. */
function isNavProvider(p: SearchProvider): boolean {
  if (NAV_PROVIDER_IDS.has(p.id)) return true
  return (p.filterIds ?? []).some((f) => NAV_PROVIDER_IDS.has(f))
}

export function useSearch({
  isOpen,
  onClose,
  onSearch,
  apiClient,
  localJobs,
  quickActions,
  searchProviders = EMPTY_PROVIDERS as SearchProvider[],
  initialActiveFilters,
  initialQuery,
}: UseSearchOptions) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  // ``initialActiveFilters`` defaults to an empty array — the
  // popup behaves exactly as before when the host doesn't pass
  // it. When non-empty, ``showFilters`` defaults to true so the
  // user can immediately see + tweak the active narrow.
  const [showFilters, setShowFilters] = useState(
    Boolean(initialActiveFilters && initialActiveFilters.length > 0),
  )
  const [activeFilters, setActiveFilters] = useState<string[]>(
    initialActiveFilters ? [...initialActiveFilters] : [],
  )
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const [semanticAvailable, setSemanticAvailable] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  // Two-pane facet scope (2026-06-04). `null` === "All" — the
  // returned `results` then equal the full merged set byte-for-byte,
  // so every pre-existing consumer/test sees no behaviour change.
  const [activeFacet, setActiveFacet] = useState<FacetKey | null>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Single AbortController for the in-flight search across all
  // providers. Each new query aborts the previous fan-out so we
  // never race stale results into the UI.
  const abortRef = useRef<AbortController | null>(null)

  // ── Latest-ref pattern (breaks the infinite-loop class) ─────────
  // Every value `performSearch` reads from props/state is mirrored
  // into a ref that we update on every render. That lets
  // `performSearch` itself be a *stable* `useCallback(…, [])` — its
  // identity never changes, so the debounced-search `useEffect`
  // (which has `performSearch` in its deps) fires ONLY when `query`
  // changes, not on every render. Without this, an unstable parent
  // prop (e.g. a fresh `searchProviders = []` literal) or an
  // internal `setRecentSearches` would re-derive `performSearch`,
  // re-fire the effect, hit `setResults([])` at the empty-query
  // branch, re-render, and loop until React bails with "Maximum
  // update depth exceeded".
  const apiClientRef = useRef(apiClient)
  const onSearchRef = useRef(onSearch)
  const localJobsRef = useRef(localJobs)
  const searchProvidersRef = useRef(searchProviders)
  const activeFiltersRef = useRef<string[]>([])
  apiClientRef.current = apiClient
  onSearchRef.current = onSearch
  localJobsRef.current = localJobs
  searchProvidersRef.current = searchProviders
  activeFiltersRef.current = activeFilters

  // Tracks whether the initial localStorage hydration of
  // `recentSearches` has run. The persist effect below short-circuits
  // until this is true so we don't overwrite the user's stored history
  // with the initial `[]` on the first paint (the load effect's
  // `setRecentSearches` doesn't apply until the next render).
  const recentSearchesHydratedRef = useRef(false)

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
      if (stored) setRecentSearches(JSON.parse(stored))
    } catch {}
    recentSearchesHydratedRef.current = true
  }, [])

  // BUG-2026-05-28-USESEARCH-INITIALFILTERS-RERENDER-LOOP —
  // `initialActiveFilters` is an array; callers that build it
  // inline (e.g. the cross-host event listener does
  // `setGlobalSearchInitialActiveFilters(d.activeFilters)` from
  // a fresh CustomEvent.detail) or tests that use renderHook
  // create a NEW array reference on every render. If we put the
  // raw array into the open-effect's dep list, the effect fires
  // on every render and calls setQuery / setResults / setActiveFilters —
  // each of which causes a re-render, which produces yet another
  // fresh array reference. Result: infinite re-render → OOM.
  //
  // Fix: drive the open-effect off a content-stable KEY (the
  // joined filter ids) AND read the live array via a ref so the
  // setter still gets the latest values. The effect now only
  // fires when the operator-visible inputs actually change.
  const initialActiveFiltersKey = (initialActiveFilters ?? []).join('\u0000')
  const initialActiveFiltersRef = useRef(initialActiveFilters)
  initialActiveFiltersRef.current = initialActiveFilters

  // Focus input when opened. Closing the palette aborts any
  // in-flight provider fan-out so a slow search doesn't continue
  // to spend bandwidth after the user dismissed the modal.
  //
  // Stack 11 PR 2 — also re-apply ``initialActiveFilters`` on
  // every open so a host that toggles the popup repeatedly
  // (e.g. Cmd+F bound on EmailHub) keeps the same scope every
  // time. Without this, the operator's chip selections from the
  // last invocation would leak into the next one if they had
  // toggled chips off.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
      // BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — when the
      // host seeds an `initialQuery` (e.g. forwarded from the
      // in-board kanban search via the `quotech:open-global-search`
      // event), prefer it over the blank reset. The existing
      // debounced-search useEffect (further down) fires
      // automatically off the next `query` change, so the palette
      // opens with results already loading. When `initialQuery` is
      // empty/undefined we fall back to the legacy blank-open.
      const seed = initialQuery && initialQuery.length > 0 ? initialQuery : ''
      setQuery(seed)
      setResults([])
      setSelectedIndex(0)
      setSearchError(null)
      setActiveFacet(null)
      const liveFilters = initialActiveFiltersRef.current
      if (liveFilters && liveFilters.length > 0) {
        setActiveFilters([...liveFilters])
        setShowFilters(true)
      }
    } else {
      abortRef.current?.abort()
      abortRef.current = null
    }
    // `initialQuery` is intentionally part of the deps so a host
    // updating it (e.g. arming a NEW search before re-opening)
    // re-seeds correctly. To avoid re-seeding mid-session every
    // time `initialQuery` changes while open, hosts pass it
    // alongside their own `isOpen` toggle (the typical pattern is
    // a `setState({ open: true, initialQuery: q })` reducer hop).
    // `initialActiveFiltersKey` is a content-derived string so
    // unstable array refs don't re-fire this effect (see the
    // OOM bug note above).
  }, [isOpen, initialActiveFiltersKey, initialQuery])

  // Client-side instant search across local jobs.
  // Stable callback (reads `localJobs` via ref) — see the latest-ref
  // block above for why this matters.
  const searchLocalJobs = useCallback(
    (searchQuery: string): SearchResult[] => {
      const jobs = localJobsRef.current
      if (!searchQuery.trim() || !jobs?.length) return []
      // W4 (2026-07-07): AND-of-tokens via the shared matching contract —
      // this was the LAST whole-string matcher ("acme roof" previously
      // missed a job whose client is Acme and site is Roof St; the board
      // filter, nav provider, and backend all already match per-token).
      const tokens = tokenizeSearchQuery(searchQuery)

      return jobs
        .filter((job) =>
          allTokensMatchFields(
            [
              String(job.job_id || ''),
              job.client,
              job.site || job.name,
              job.description,
            ],
            tokens,
          ),
        )
        .slice(0, 15)
        .map((job) => ({
          id: String(job.job_id),
          type: 'job' as const,
          title: `${job.job_id} — ${job.site || job.name || 'Untitled'}`,
          subtitle: job.client || 'No Client',
          description: job.description?.slice(0, 120) || undefined,
          icon: 'Briefcase',
          color: 'blue',
          url: `/jobs/${job.job_id}`,
          metadata: { status: job.status || 'active' },
        }))
    },
    [],
  )

  // Helper: dedupe a list of results by `${providerId|type}-${id}`,
  // preserving order. Used to merge legacy API results, provider
  // results, and local results without showing the same row twice.
  const dedupe = (rows: SearchResult[]): SearchResult[] => {
    const seen = new Set<string>()
    const out: SearchResult[] = []
    for (const r of rows) {
      const key = `${r.providerId || r.type}-${r.id}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(r)
    }
    return out
  }

  // Frecency map — loaded once per palette OPEN (not per keystroke) so
  // ranking is stable within a session of typing. Selections recorded
  // mid-session update localStorage immediately but only re-rank on the
  // next open (deliberate: rows must not jump under the pointer).
  const frecencyRef = useRef<FrecencyMap>({})
  useEffect(() => {
    if (isOpen) frecencyRef.current = loadFrecency()
  }, [isOpen])

  // Stable sort by section order (board-first sectioned palette), then
  // frecency (W2, 2026-07-07): rows the operator repeatedly picks float
  // up WITHIN their section. Rows with no selection history score 0, so
  // a fresh profile ranks byte-identically to the pre-frecency arrival
  // order (the index tiebreak). Rows without a `section` (legacy api
  // results, local jobs, the master SPA's flat providers) all map to the
  // same sentinel order — sectionless output equals the flat merge.
  // Index-tagged to guarantee stability regardless of the engine's
  // `Array.sort` stability.
  const orderBySection = (rows: SearchResult[]): SearchResult[] => {
    const now = Date.now()
    const fmap = frecencyRef.current
    return rows
      .map((r, i) => [r, i, frecencyScore(fmap[frecencyKey(r)], now)] as const)
      .sort((a, b) => {
        const delta = sectionOrder(a[0].section) - sectionOrder(b[0].section)
        if (delta !== 0) return delta
        if (a[2] !== b[2]) return b[2] - a[2]
        return a[1] - b[1]
      })
      .map(([r]) => r)
  }

  // Perform search: instant local + async API + provider fan-out.
  //
  // Provider fan-out (PR 3b) runs in parallel with the legacy
  // `apiClient.post('/api/search/global/')` call. Per-provider
  // failures are isolated via `Promise.allSettled` so one
  // misbehaving module does not take down the whole palette.
  //
  // ⚠ Stable callback (deps = `[]`) — DO NOT add reactive values
  // back into the dep array. All mutable inputs are read via refs
  // (apiClientRef, onSearchRef, searchProvidersRef, activeFiltersRef,
  // localJobsRef). `setRecentSearches` uses the functional form so
  // the previous `recentSearches` value can be read without it being
  // a closure dep. localStorage persistence is split into its own
  // effect below. This is what stops the infinite re-render loop —
  // see the latest-ref block above for the full explanation.
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setResults([])
        setSearchError(null)
        return
      }

      const apiClient = apiClientRef.current
      const onSearch = onSearchRef.current
      const searchProviders = searchProvidersRef.current
      const activeFilters = activeFiltersRef.current

      // Cancel the previous fan-out before kicking off a new one.
      // Provider implementations that ignore `signal` still finish
      // their requests, but their late results are dropped because
      // we check `controller.signal.aborted` before calling
      // `setResults`.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const { signal } = controller

      const localResults = searchLocalJobs(searchQuery)
      if (localResults.length > 0) {
        setResults(localResults)
      }

      const hasFanOut = !!apiClient || searchProviders.length > 0
      if (!hasFanOut) {
        if (localResults.length === 0) setSearchError(null)
        return
      }

      setIsLoading(true)
      setSearchError(null)

      // Filter providers based on the active filter chips. Each
      // provider declares which filter ids it answers to via
      // `filterIds`; default is `[provider.id]`.
      // Nav/hub providers are ALWAYS eligible — see NAV_PROVIDER_IDS.
      // Content-type chips narrow what kind of CONTENT shows; they must
      // not suppress navigation targets (the operator complaint:
      // searching "email" returned messages but never the Email hub).
      const eligibleProviders = activeFilters.length === 0
        ? searchProviders
        : searchProviders.filter(
            (p) =>
              isNavProvider(p)
              || (p.filterIds ?? [p.id]).some((f) => activeFilters.includes(f)),
          )

      // BUG-2026-05-25-GLOBAL-SEARCH-FANOUT-DROPS-FIRST-PROVIDER:
      // When the host doesn't pass an `apiClient` (e.g. tenant SPAs
      // like apps/newmark + apps/sam that ship `searchProviders`
      // only), the previous implementation did
      //   `[apiTask, ...providerTasks].filter(t => t !== null)`
      // which dropped the `null` and SHIFTED every provider one slot
      // to the left. The downstream
      //   `[apiSettled, ...providerSettled] = await allSettled(...)`
      // then put the FIRST provider's settled result into
      // `apiSettled` — which is only consumed when `apiClient` is
      // truthy. Net effect: the first provider (almost always the
      // ONLY provider in apps/newmark + apps/sam) was silently
      // discarded, so typing "hudson" returned zero rows even though
      // the backend returned 8 cards and the provider resolved them
      // correctly.
      //
      // Fix (still upheld by the progressive merge below): each
      // task — the optional api task AND every provider — appends to
      // a SHARED `collected` array in its own `.then`, then we
      // `Promise.allSettled([...])` them together. There is no
      // positional destructuring of the settled array, so an
      // optional head element can no longer shift providers into the
      // wrong slot. The trade-off is a tiny bit more code; the upside
      // is the bug class can't
      // recur with the same shape.
      // BUG-2026-05-28-CHIP-FILTERS-DONT-NARROW-RESULTS — chips
      // gate which PROVIDERS run (via `eligibleProviders` above),
      // but providers like Kanban legitimately return >1 result
      // type per call (`board` + `card` + `lane`). Without
      // post-fetch type filtering, toggling "Cards" still shows
      // boards and lanes — counter-intuitive. The cross-board
      // fallback CTA from MR 3407 also relies on this so it can
      // scope to exactly the operator-relevant kinds without
      // each provider needing to take a per-call types arg.
      //
      // Computed UP FRONT (only depends on activeFilters) so the
      // progressive merge can filter each batch as it arrives.
      //
      // Semantics (industry-standard chip composition):
      //   - OR within each group: ticking [card, board] keeps
      //     any row whose `type` is card OR board.
      //   - AND across groups: ticking [kanban, card] keeps
      //     rows whose providerId is kanban AND whose type is
      //     card (the operator wants "kanban cards", not "any
      //     kanban hit or any card").
      const KNOWN_TYPE_FILTERS = new Set([
        'job', 'quote', 'file', 'email', 'card', 'board',
        'lane', 'contact', 'qa',
      ])
      const typeFilters = activeFilters.filter((f) => KNOWN_TYPE_FILTERS.has(f))
      const providerFilters = activeFilters.filter((f) => !KNOWN_TYPE_FILTERS.has(f))
      const applyTypeNarrow = typeFilters.length > 0
      const applyProviderNarrow = providerFilters.length > 0
      const keepProviderRow = (r: SearchResult): boolean => {
        // Navigation/page hits are exempt from content-type + provider
        // narrowing — they are a meta-layer, not narrowable content.
        // Without this, pre-scoping the popup to ['email','card',…]
        // would strip the Email-hub page result the operator wants.
        if (NAV_RESULT_TYPES.has(r.type)) return true
        if (applyTypeNarrow && !typeFilters.includes(r.type)) return false
        if (
          applyProviderNarrow
          && (!r.providerId || !providerFilters.includes(r.providerId))
        ) {
          return false
        }
        return true
      }
      const shouldFilter = applyTypeNarrow || applyProviderNarrow
      // Local jobs have no providerId, so the provider-narrow half
      // can't match them — drop them entirely under a provider narrow.
      const filteredLocalResults = shouldFilter
        ? localResults.filter((r) => {
            if (applyTypeNarrow && !typeFilters.includes(r.type)) return false
            if (applyProviderNarrow) return false
            return true
          })
        : localResults

      // ── Progressive, section-ordered merge ──────────────────────
      // `collected` accumulates every source's rows as they settle.
      // After each arrival we dedupe + section-sort + `setResults`,
      // so the synchronous on-board provider paints its "On this
      // board" rows in the first tick while the network tiers (jobs,
      // emails, other boards) stream in below — no "spinner until
      // everything returns" wait. Order is derived purely from
      // `SEARCH_SECTIONS`, so arrival order between providers can't
      // scramble the sections.
      const collected: SearchResult[] = [...filteredLocalResults]
      const paint = () => {
        if (signal.aborted) return
        setResults(orderBySection(dedupe(collected)))
      }
      // Paint the instant local tier immediately (if any).
      if (filteredLocalResults.length > 0) paint()

      const apiTask = apiClient
        ? apiClient
            .post('/api/search/global/', {
              query: searchQuery,
              types: activeFilters.length > 0 ? activeFilters : undefined,
              limit: 10,
              semantic: true,
            })
            .then((resp: any) => {
              if (signal.aborted) return
              const data = resp?.data || resp
              const rows = ((data?.top_results || []) as SearchResult[])
              // The legacy endpoint already honours `types` server
              // side, but post-filter so a stale build can't leak
              // unrequested kinds.
              const kept = shouldFilter ? rows.filter(keepProviderRow) : rows
              collected.push(...kept)
              setSemanticAvailable(!!data?.semantic_available)
              paint()
            })
            .catch((reason: any) => {
              const status = reason?.response?.status
              const msg =
                status === 401
                  ? 'Authentication expired — showing local results only'
                  : status === 403
                    ? 'Access denied — showing local results only'
                    : 'Server search unavailable — showing local results only'
              setSearchError(msg)
              if (import.meta.env.DEV) {
                console.error('[GlobalSearch] API error:', reason)
              }
            })
        : null

      const providerErrors: string[] = []
      // W5 perf: session query cache + per-provider timing. A provider whose
      // (id, query) pair is cached (30s TTL LRU) paints instantly with zero
      // network; misses fan out in parallel as before and populate the cache
      // on success. Timing lands on the telemetry seam so slow providers are
      // measurable in production (`window.__QT_OBS__.searchTelemetry`).
      const fanOutStart = performance.now()
      const providerMs: Record<string, number> = {}
      const cacheHits: string[] = []
      const providerTasks = eligibleProviders.map((p) => {
        const cached = getCachedResults(p.id, searchQuery)
        if (cached) {
          cacheHits.push(p.id)
          providerMs[p.id] = 0
          const kept = shouldFilter ? cached.filter(keepProviderRow) : cached
          collected.push(...kept)
          paint()
          return Promise.resolve()
        }
        const started = performance.now()
        return p
          .search(searchQuery, signal)
          .then((rows) => {
            if (signal.aborted) return
            providerMs[p.id] = Math.round(performance.now() - started)
            const tagged = rows.map((r) => ({
              ...r,
              providerId: r.providerId ?? p.id,
            }))
            // Cache the UNfiltered tagged rows — a later query with
            // different chips must be able to re-filter from the full set.
            setCachedResults(p.id, searchQuery, tagged)
            const kept = shouldFilter ? tagged.filter(keepProviderRow) : tagged
            collected.push(...kept)
            paint()
          })
          .catch((err) => {
            if (signal.aborted) return
            providerMs[p.id] = Math.round(performance.now() - started)
            providerErrors.push(p.id)
            if (import.meta.env.DEV) {
              console.error(`[GlobalSearch] provider "${p.id}" failed:`, err)
            }
          })
      })

      try {
        await Promise.allSettled([
          ...(apiTask ? [apiTask] : []),
          ...providerTasks,
        ])
        if (signal.aborted) return

        if (providerErrors.length > 0 && !apiClient) {
          setSearchError(
            `Some sources unavailable (${providerErrors.join(', ')}) — showing partial results`,
          )
        }

        const merged = orderBySection(dedupe(collected))
        // Final authoritative paint (covers the all-empty case where
        // no incremental paint ran).
        setResults(merged)

        // Zero-result telemetry — emitted ONLY at the authoritative final
        // merge (never on incremental paints, which are legitimately empty
        // early). The leading indicator for relevance gaps: a query that
        // consulted every eligible source and still came back empty.
        if (merged.length === 0) {
          emitSearchTelemetry({
            kind: 'zero_results',
            query: searchQuery,
            providerIds: eligibleProviders.map((p) => p.id),
            activeFilters: [...activeFilters],
          })
        }

        // W5 timing — one event per completed fan-out (aborted fan-outs
        // never reach here, so timings are clean end-to-end measurements).
        if (eligibleProviders.length > 0) {
          emitSearchTelemetry({
            kind: 'search_timing',
            query: searchQuery,
            totalMs: Math.round(performance.now() - fanOutStart),
            providerMs,
            cacheHits,
            resultCount: merged.length,
          })
        }

        if (searchQuery.length >= RECENT_SEARCH_MIN_LEN) {
          setRecentSearches((prev) => pushRecentSearch(prev, searchQuery))
        }

        onSearch?.(searchQuery, merged)
      } finally {
        if (!signal.aborted) setIsLoading(false)
      }
    },
    [searchLocalJobs],
  )

  // Persist recent searches to localStorage as a side-effect of state
  // changes — keeps `performSearch` free of `recentSearches` as a
  // closure-dep (which was one half of the re-render loop).
  // Skipped until hydration completes so we don't stomp the user's
  // saved history with the initial `[]`.
  useEffect(() => {
    if (!recentSearchesHydratedRef.current) return
    try {
      localStorage.setItem(
        RECENT_SEARCHES_KEY,
        JSON.stringify(recentSearches),
      )
    } catch {}
  }, [recentSearches])

  // Debounced search. Each new query resets the facet scope back to
  // "All" — the previous facet may not exist in the new result set,
  // and a stale scope would otherwise show an empty centre.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    setActiveFacet(null)
    if (query.trim()) {
      debounceRef.current = setTimeout(() => performSearch(query), 200)
    } else {
      setResults([])
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, performSearch])

  // Facets are derived from the FULL merged result set so the rail
  // counts are accurate regardless of the active scope.
  const facets: Facet[] = useMemo(() => computeFacets(results), [results])

  // The centre renders the scoped slice. When `activeFacet` is null
  // this is the same array reference as `results` (see filterByFacet),
  // so keyboard-nav indices and existing consumers are unaffected.
  const visibleResults = useMemo(
    () => filterByFacet(results, activeFacet),
    [results, activeFacet],
  )

  // All selectable items (keyboard nav targets the VISIBLE slice).
  const selectableItems = useMemo(() => {
    if (!query.trim()) {
      return quickActions.map((a) => ({ ...a, isAction: true }))
    }
    return visibleResults.map((r) => ({ ...r, isAction: false }))
  }, [query, visibleResults, quickActions])

  // Reset the highlighted row whenever the operator changes scope so
  // the selection never points past the (now shorter) visible slice.
  useEffect(() => {
    setSelectedIndex(0)
  }, [activeFacet])

  const recordRecentSearch = useCallback((searchQuery: string) => {
    setRecentSearches((prev) => pushRecentSearch(prev, searchQuery))
  }, [])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Facet-rail cycling — Alt/Option + ↑/↓ moves the active SOURCE
      // facet (All → Pages → Jobs → Emails → …) without leaving the
      // input, so result navigation is fully keyboard-driven (operator
      // can scope by source, then plain ↑/↓ walks the rows). Wraps at
      // both ends. Plain ↑/↓ keeps moving result rows unchanged.
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault()
        const rail: (FacetKey | null)[] = [null, ...facets.map((f) => f.key)]
        if (rail.length > 1) {
          const cur = rail.indexOf(activeFacet)
          const delta = e.key === 'ArrowDown' ? 1 : -1
          const nextIdx = (cur + delta + rail.length) % rail.length
          setActiveFacet(rail[nextIdx])
        }
        return
      }
      switch (e.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, selectableItems.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          const selected = selectableItems[selectedIndex]
          if (selected) {
            if (query.trim()) recordRecentSearch(query)
            // Selection telemetry (keyboard path — the click path emits in
            // SearchResults). Quick actions are excluded: they're commands,
            // not search relevance signal.
            if (!selected.isAction) {
              emitSearchTelemetry({
                kind: 'result_selected',
                query,
                index: selectedIndex,
                resultId: String((selected as SearchResult).id),
                resultType: (selected as SearchResult).type,
                providerId: (selected as SearchResult).providerId,
              })
              recordFrecencySelection(selected as SearchResult)
            }
            if ('url' in selected && selected.url) window.location.href = selected.url
            else if ('action' in selected && selected.action) (selected as any).action()
            onClose()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isOpen,
    selectableItems,
    selectedIndex,
    onClose,
    query,
    recordRecentSearch,
    facets,
    activeFacet,
    setActiveFacet,
  ])

  // Scroll selected into view
  useEffect(() => {
    if (listRef.current) {
      const selectedEl = listRef.current.querySelector(`[data-index="${selectedIndex}"]`)
      selectedEl?.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  const toggleFilter = (filterId: string) => {
    setActiveFilters((prev) =>
      prev.includes(filterId) ? prev.filter((f) => f !== filterId) : [...prev, filterId]
    )
  }

  return {
    query,
    setQuery,
    // `results` is the facet-scoped slice. When activeFacet is null
    // (the default) it is the full merged set — unchanged contract.
    results: visibleResults,
    /** Full merged-result count, independent of the active facet
     *  scope. Drives the rail's "All" badge. */
    totalResults: results.length,
    /** Ordered source facets (with counts) for the right rail. */
    facets,
    activeFacet,
    setActiveFacet,
    isLoading,
    selectedIndex,
    showFilters,
    setShowFilters,
    activeFilters,
    recentSearches,
    semanticAvailable,
    searchError,
    inputRef,
    listRef,
    toggleFilter,
    recordRecentSearch,
  }
}

// Hook for ⌘F / Ctrl+F keyboard shortcuts (⌘K / Ctrl+K still works).
//
// BUG-0040: operators kept reaching for the browser-native ⌘F to
// "search" and getting the native Find-in-page dialog, which only
// searches DOM that's currently rendered — useless on a SPA with
// server-paginated lists (kanban cards, jobs, emails, contacts).
// In-app search now binds ⌘F / Ctrl+K universally. Native Find is
// still reachable via the browser menu / Edit > Find.
//
// @deprecated Prefer the shell-owned ⌘F authority — `useLayoutState`
// (master SPA via DashboardLayout) or the tenant CommandPaletteProvider
// — both of which consult the in-page find registry. This standalone
// hook currently has NO callers; it is kept for backward-compat only.
// If adopted, note it (a) TOGGLES open/close, and (b) like the shell,
// yields ⌘F to a registered FindController so it doesn't fight an
// in-page find surface (BUG-2026-06-10-CMDF-POPUP-SHADOWED).
export function useGlobalSearch() {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      const key = e.key.toLowerCase()
      if (key === 'f' || key === 'k') {
        e.preventDefault()
        e.stopPropagation()
        // ⌘F yields to an in-page find surface when one is registered;
        // ⌘K always toggles the palette.
        if (key === 'f') {
          const controller = getActiveFindController()
          if (controller) {
            controller.open()
            return
          }
        }
        setIsOpen((prev) => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [])

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen((prev) => !prev),
  }
}
